import React, { useState } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { getTurnstileToken } from '@/lib/turnstile';
import SecureFormGate from '@/components/hipaatizer/SecureFormGate';

export default function SMSWidget() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [hipaaConsent, setHipaaConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, '');
    if (!form.message.trim() || !hipaaConsent) return;
    if (digits.length < 10) {
      setError('A valid phone number is required so we can text you back.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await base44.functions.invoke('sendSMS', {
        message: form.message,
        from_name: form.name,
        from_phone: form.phone,
        turnstile_token: (await getTurnstileToken('sendSMS')) || undefined,
      });
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-24 left-5 z-50">
      {open && (
        <div className="mb-3 w-80 max-w-[calc(100vw-2.5rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-amber-400" />
              <span className="font-semibold text-sm">Send Us a Text</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close text message panel" className="text-slate-400 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {sent ? (
            <div className="p-6 text-center" role="status" aria-live="polite">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Send className="w-5 h-5 text-green-600" />
              </div>
              <p className="font-semibold text-slate-900 mb-1">Message sent!</p>
              <p className="text-sm text-slate-500 mb-4">We'll text you back at (727) 607-8699 shortly.</p>
              <a
                href="sms:7276078699"
                className="inline-flex items-center gap-2 text-sm text-amber-600 font-semibold hover:underline"
              >
                <MessageSquare className="w-4 h-4" />
                Open your SMS app →
              </a>
            </div>
          ) : (
            <SecureFormGate placement="text-us" title="Send us a text" mode="link" compact>
            <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
              <p className="text-xs text-slate-500">
                Text us at <a href="sms:7276078699" className="font-semibold text-amber-600">(727) 607-8699</a> — we respond fast!
              </p>
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-amber-400"
              />
              <input
                type="tel"
                placeholder="Your phone * (so we can text back)"
                value={form.phone}
                onChange={e => { setForm(f => ({ ...f, phone: e.target.value })); setError(''); }}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-amber-400"
              />
              <p className="-mt-1 text-[11px] text-slate-400">We'll text you back — a phone number is required.</p>
              <textarea
                placeholder="Type your message..."
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                rows={3}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-amber-400 resize-none"
              />
              {/* HIPAA Consent */}
              <div className={`flex items-start gap-2 p-2.5 rounded-lg border ${!hipaaConsent && error ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                <input
                  id="sms-hipaa-consent"
                  type="checkbox"
                  checked={hipaaConsent}
                  onChange={e => setHipaaConsent(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-amber-600 cursor-pointer flex-shrink-0"
                />
                <label htmlFor="sms-hipaa-consent" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                  I acknowledge the{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-600 font-semibold underline">HIPAA Notice of Privacy Practices</a>
                  {' '}and consent to be contacted by Trinity Dental Arts.
                </label>
              </div>
              {error && <p role="alert" className="text-red-500 text-xs">{error}</p>}
              <button
                type="submit"
                disabled={sending || !form.message.trim() || !hipaaConsent || form.phone.replace(/\D/g, '').length < 10}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </form>
            </SecureFormGate>
          )}
        </div>
      )}

      {/* Trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-14 h-14 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
        aria-label="Send us a text"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>
    </div>
  );
}